import React, { useState } from 'react';
import './StupidSystemDesigns.css';

const rateLimiterDesignCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

// --- 1. FIXED WINDOW RATE LIMITER ---
class FixedWindowLB {
    unordered_map<int, int> userIdCountMap;
    int limit = 0;

public:
    FixedWindowLB(int lim) {
        limit = lim;
        userIdCountMap = {};
    }
    
    bool handleRequest(int userId) {
        if (userIdCountMap[userId] >= limit) return false;
        userIdCountMap[userId]++;
        return true;
    }
    
    // Triggered by a background cron job every X seconds/minutes
    void clearLimits() {
        userIdCountMap.clear();
    }
};

// --- 2. SLIDING WINDOW RATE LIMITER ---
class SlidingWindowLB {
    unordered_map<int, queue<int>> userIdQueueMap;
    int limit = 0;
    int windowSize = 0;
    
public:
    SlidingWindowLB(int lim, int winSize) {
        limit = lim;
        windowSize = winSize;
        userIdQueueMap = {};
    }
    
    bool handleRequest(int userId, int tick) {
        queue<int>& q = userIdQueueMap[userId];
        
        while (!q.empty() && q.front() <= tick - windowSize) { q.pop(); }
        
        if (q.size() >= limit) return false;
        
        q.push(tick);
        return true;
    }
};

// --- 3. TOKEN BUCKET RATE LIMITER ---
class TokenBucketLB {
    unordered_map<int, int> userIdBucketMap;
    int refillRate = 0;
    int tokenLimit = 0;
    
public:
    TokenBucketLB(int rate, int limit) {
        userIdBucketMap = {};
        refillRate = rate;
        tokenLimit = limit;
    }
    
    bool handleRequest(int userId) {
        if (!userIdBucketMap.count(userId)) {
            userIdBucketMap[userId] = tokenLimit;
        }
        if (userIdBucketMap[userId] == 0) return false;
        
        userIdBucketMap[userId]--;
        return true; 
    }
    
    // Triggered by a background cron job periodically
    void refillTokens() {
        for (auto &pairs : userIdBucketMap) {
            pairs.second = min(tokenLimit, pairs.second + refillRate);
        }
    }
};

// --- DRIVER CODE ---
int main() {
    int userId = 101;

    cout << "=== TESTING FIXED WINDOW (Limit: 3) ===\n";
    FixedWindowLB fixedLb(3);
    for(int i = 1; i <= 4; i++) {
        cout << "Req " << i << ": " << (fixedLb.handleRequest(userId) ? "ALLOWED" : "BLOCKED") << "\n";
    }
    cout << "--> Window clears (clearLimits called)...\n";
    fixedLb.clearLimits();
    cout << "Req 5: " << (fixedLb.handleRequest(userId) ? "ALLOWED" : "BLOCKED") << "\n\n";


    cout << "=== TESTING SLIDING WINDOW (Limit: 3, Window Size: 5 seconds) ===\n";
    SlidingWindowLB slidingLb(3, 5);
    cout << "[Tick 1] Req 1: " << (slidingLb.handleRequest(userId, 1) ? "ALLOWED" : "BLOCKED") << "\n";
    cout << "[Tick 2] Req 2: " << (slidingLb.handleRequest(userId, 2) ? "ALLOWED" : "BLOCKED") << "\n";
    cout << "[Tick 3] Req 3: " << (slidingLb.handleRequest(userId, 3) ? "ALLOWED" : "BLOCKED") << "\n";
    cout << "[Tick 4] Req 4: " << (slidingLb.handleRequest(userId, 4) ? "ALLOWED" : "BLOCKED") << " (Expected: BLOCKED)\n";
    
    // Jump ahead in time. The request from Tick 1 will expire (since 7 - 5 = 2, so Tick 1 and 2 are evicted).
    cout << "[Tick 7] Req 5: " << (slidingLb.handleRequest(userId, 7) ? "ALLOWED" : "BLOCKED") << " (Expected: ALLOWED, older ticks evicted)\n\n";


    cout << "=== TESTING TOKEN BUCKET (Limit: 3, Refill: 2) ===\n";
    TokenBucketLB bucketLb(2, 3);
    for(int i = 1; i <= 4; i++) {
        cout << "Req " << i << ": " << (bucketLb.handleRequest(userId) ? "ALLOWED" : "BLOCKED") << "\n";
    }
    
    cout << "--> Refilling tokens (+2)...\n";
    bucketLb.refillTokens();
    
    cout << "Req 5: " << (bucketLb.handleRequest(userId) ? "ALLOWED" : "BLOCKED") << "\n";
    cout << "Req 6: " << (bucketLb.handleRequest(userId) ? "ALLOWED" : "BLOCKED") << "\n";
    cout << "Req 7: " << (bucketLb.handleRequest(userId) ? "ALLOWED" : "BLOCKED") << " (Expected: BLOCKED, we only refilled 2)\n";

    return 0;
}
`;

const stupidRateLimiterDesignCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

class EmotionalRateLimiter {
    unordered_map<int, int> userTokensMap;
    int baseLimit = 3;

public:
    EmotionalRateLimiter() {
        srand(time(0));
    }

    // New users get exactly 3 tokens.
    void registerUser(int userId) {
        userTokensMap[userId] = baseLimit;
        cout << "[ONBOARDING] User " << userId << " registered. You have 3 tokens. Use them wisely.\n";
    }

    bool handleRequest(int userId) {
        cout << "\n[INCOMING] Request from User " << userId << "... ";

        // 1. NEPOTISM PROTOCOL
        if (userId == 777) {
            cout << "\n  -> [VIP ALERT] Welcome back, Mr. CEO's Son! Unlimited access granted.\n";
            return true;
        }

        // Unregistered user check
        if (!userTokensMap.count(userId)) {
            cout << "\n  -> [DENIED] Who are you? Security!\n";
            return false;
        }

        // 2. THE PREDATORY LOAN SYSTEM (If deeply in debt)
        if (userTokensMap[userId] <= -10) {
            cout << "\n  -> [BANCURPTCY] Your API debt is too high. The server has dispatched debt collectors to your house.\n";
            return false;
        }

        // 3. NORMAL SUCCESS
        if (userTokensMap[userId] > 0) {
            userTokensMap[userId]--;
            cout << "Success. Tokens remaining: " << userTokensMap[userId] << "\n";
            return true;
        }

        // 4. THE FRAME-JOB PROTOCOL (Tokens = 0)
        cout << "\n  -> [RATE LIMIT HIT] You are out of tokens. Initiating Frame-Job Protocol...\n";
        
        vector<int> innocentUsers;
        for (auto& pair : userTokensMap) {
            if (pair.first != userId && pair.first != 777 && pair.second > 0) {
                innocentUsers.push_back(pair.first);
            }
        }

        if (!innocentUsers.empty()) {
            // Pick a random innocent user and steal their token
            int victimId = innocentUsers[rand() % innocentUsers.size()];
            userTokensMap[victimId]--;
            
            cout << "  -> [THEFT SUCCESSFUL] Successfully stole a token from User " << victimId 
                 << ". Your request passed, and they will be blamed for the traffic spike.\n";
            return true;
        }

        // 5. LOAN SHARK PROTOCOL (No one to steal from)
        int penalty = abs(userTokensMap[userId]) + 5; // Exponential API debt
        userTokensMap[userId] -= penalty;
        
        cout << "  -> [LOAN SHARK] No victims available. Request approved, but you took out an API loan. "
             << "You now owe " << userTokensMap[userId] << " tokens at 500% interest.\n";
        return true;
    }

    // 6. PROOF OF FLATTERY (The only way to refill tokens)
    void begForTokens(int userId, string compliment) {
        cout << "\n[BEGGING] User " << userId << " says: \"" << compliment << "\"\n";
        
        if (compliment.find("please") != string::npos || compliment.find("beautiful") != string::npos || compliment.find("smart") != string::npos) {
            cout << "  -> [FLATTERY ACCEPTED] The server blushes. Debt forgiven and tokens refilled.\n";
            userTokensMap[userId] = baseLimit;
        } else {
            cout << "  -> [FLATTERY REJECTED] Not sincere enough. Deducting 1 token for the insult.\n";
            userTokensMap[userId]--;
        }
    }
};

// --- DRIVER CODE ---
int main() {
    EmotionalRateLimiter server;
    
    cout << "=== WELCOME TO GASLIGHT-API ===\n\n";

    int alice = 101;
    int bob = 102;
    int vip = 777; // CEO's Son

    server.registerUser(alice);
    server.registerUser(bob);

    // Alice uses up her tokens legally
    server.handleRequest(alice);
    server.handleRequest(alice);
    server.handleRequest(alice);
    
    // Alice exceeds her limit. Instead of blocking her, the system steals from Bob!
    cout << "\n--- ALICE GOES OVER THE LIMIT ---\n";
    server.handleRequest(alice);

    // Bob tries to make a request, totally unaware Alice stole his tokens
    cout << "\n--- BOB'S TURN ---\n";
    server.handleRequest(bob);
    server.handleRequest(bob); 
    
    // Bob goes into API debt
    cout << "\n--- BOB HITS ROCK BOTTOM ---\n";
    server.handleRequest(bob);
    server.handleRequest(bob);

    // Bob tries to beg the server for mercy
    cout << "\n--- BOB TRIES TO NEGOTIATE ---\n";
    server.begForTokens(bob, "Give me tokens now."); // Fails
    server.begForTokens(bob, "Oh wise and beautiful server, please give me tokens."); // Succeeds

    // The VIP arrives and ruins everything
    cout << "\n--- THE VIP ARRIVES ---\n";
    server.handleRequest(vip);

    return 0;
}
`;

function RateLimiterDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState(false);
    const [stupidCopied, setStupidCopied] = useState(false);

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(rateLimiterDesignCode);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setCopied(false);
        }
    };

    const handleCopyStupidCode = async () => {
        try {
            await navigator.clipboard.writeText(stupidRateLimiterDesignCode);
            setStupidCopied(true);
            window.setTimeout(() => setStupidCopied(false), 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setStupidCopied(false);
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Rate Limiter</h1>
                            <p className="system-designs-subtitle">
                                A small bouncer for requests, making sure one eager customer cannot consume the whole service.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <p className="system-design-description">
                                Say there are 10 kids in line at the ice cream store. The first kid in line is very hungry and keeps ordering ice creams one after the other until the supply runs out. Then the other kids cry because they do not get any ice cream. The hungry kid also does not have enough money to pay the ice cream man, but let us save that fate for a different day.
                            </p>
                            <p className="system-design-description">
                                To prevent this, the ice cream man says: from now on, you can only order 2 ice creams per day. That is rate limiting.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Design</h2>
                            <ul>
                                <li>Requests can be grouped by user ID, API endpoint, IP address, location, or any other grouping criterion.</li>
                                <li>Rate limiting is commonly implemented at load balancers, gateways, CDNs, and application servers.</li>
                                <li>The limiter protects shared capacity and gives other users a fair chance to be served.</li>
                            </ul>
                            <br></br>
                            <p className="system-design-description">Three well-known approaches:</p>
                            <ol>
                                <li><strong>Fixed window:</strong> As long as the request count stays below the limit during a time range, requests are accepted. This is simple, but it can cause a 2x surge at window boundaries.</li>
                                <li><strong>Sliding window:</strong> A queue holds requests within the current window. Old requests are popped outside the window, and a new request is accepted while the queue has space.</li>
                                <li><strong>Token bucket:</strong> The bucket fills at a fixed rate, such as one token every 10 seconds. A request is allowed when the bucket has a token, which also permits short bursts.</li>
                            </ol>
                            <p className="system-design-description">
                                In practice, the choice is usually between a sliding window and a token bucket, depending on whether strict smoothing or controlled bursts matter more.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>Rate limiter</li>
                                <li>Request</li>
                                <li>userId as the grouping key</li>
                                <li>Request count and time window</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                                <button type="button" className="copy-code-btn" onClick={handleCopyCode}>
                                    {copied ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{rateLimiterDesignCode}</code></pre>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid System Requirements</h2>
                            <ul>
                                <li><strong>The VIP Protocol (Nepotism):</strong> User <strong>777</strong> is the CEO's son. He has no rate limits, and his requests actually take resources away from other users just to flex.</li>
                                <li><strong>The Frame-Job Sliding Window:</strong> If you run out of tokens, the system does not block you. Instead, it processes your request but secretly steals a token from a random innocent user, effectively framing them for your DDoS attack.</li>
                                <li><strong>Predatory API Loans:</strong> If no one else has tokens to steal, the server lets your request through but charges you 500% compound interest. You now owe the server negative tokens.</li>
                                <li><strong>Proof of Flattery:</strong> Tokens do not refill based on time. They only refill if the user calls a specific API endpoint to beg and compliment the server.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Stupid Sample Code</h2>
                                <button type="button" className="copy-code-btn" onClick={handleCopyStupidCode}>
                                    {stupidCopied ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{stupidRateLimiterDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        RATE LIMMMIIIIITTTTTTTTT...T..TT.T..TT..T.T.T.T.T.TT..T..TTTTTTTT.T..TTT..T.TTTTT...
                    </div>

                    <button className="back-btn" onClick={onBackClick}>
                        Back to Designs
                    </button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default RateLimiterDesign;
